<template>
  <!-- Скелетон -->
  <div v-if="loading" :class="gridClass">
    <div
      v-for="i in skeletonCount"
      :key="i"
      class="border rounded-xl overflow-hidden"
    >
      <div class="w-full pt-[133%] bg-gray-100 animate-pulse" />
      <div class="p-3">
        <div class="min-h-[4.25rem] space-y-2">
          <div class="h-4 bg-gray-100 animate-pulse rounded w-3/4" />
          <div class="h-4 bg-gray-100 animate-pulse rounded w-1/2" />
        </div>
        <div class="flex items-end justify-between gap-3 pt-2">
          <div class="h-5 w-20 bg-gray-100 animate-pulse rounded" />
          <div class="h-11 w-11 bg-gray-100 animate-pulse rounded-full" />
        </div>
      </div>
    </div>
  </div>

  <!-- Картки -->
  <div v-else-if="items.length" :class="gridClass">
    <CatalogProductCard
      v-for="item in items"
      :key="item.key"
      :product="item.product"
      :variant="item.variant ?? undefined"
    />
  </div>

  <!-- Порожньо -->
  <div v-else-if="emptyText" class="py-16 text-center text-gray-500">
    {{ emptyText }}
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { GridItem } from "~/composables/catalog/gridItems";

/**
 * Єдиний грід карток товару: сітка, скелетон і порожній стан.
 * До REFACTOR_PLAN п.9 ця розмітка існувала п'ятьма копіями у двох
 * різних конвенціях відступів.
 *
 * Класи сітки — статичні рядки з мапи, а не зібрані з пропа: Tailwind
 * сканує вихідний код, і склеєний у рантаймі клас у білд не потрапляє.
 */
const GRID_CLASS = {
  // каталог і SEO-лендінги: щільніше, більше карток в екрані
  compact: "grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4",
  // головна і бажане: просторіше
  roomy: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6",
} as const;

const props = withDefaults(
  defineProps<{
    items?: GridItem[];
    /** Показати скелетон замість карток. Умову рахує сторінка. */
    loading?: boolean;
    skeletonCount?: number;
    /** Порожній рядок — не рендерити порожній стан узагалі. */
    emptyText?: string;
    density?: keyof typeof GRID_CLASS;
  }>(),
  {
    items: () => [],
    loading: false,
    skeletonCount: 8,
    emptyText: "",
    density: "compact",
  },
);

const gridClass = computed(
  () => GRID_CLASS[props.density] ?? GRID_CLASS.compact,
);
</script>
